import { useState, useEffect, useCallback } from 'react';
import { useParams, useOutletContext, useNavigate, Link } from 'react-router-dom';
import { ClipboardList, Play, Bug, ArrowUp, ArrowDown } from 'lucide-react';
import toast from 'react-hot-toast';
import {
  useRunList,
  useRunDetail,
  useRunResults,
  useCreateTestRun,
} from '../hooks/useTestRuns';
import { fetchActiveRun } from '../services/testRunService';
import Spinner from '../components/ui/Spinner';
import type { ProjectDetail } from '../services/projectService';
import type { TestRunItem, TestRunResultItem } from '../services/testRunService';

// --- Constants ---

const RESULT_LABEL: Record<string, string> = {
  NOT_RUN: '미실행',
  PASS: 'PASS',
  FAIL: 'FAIL',
  BLOCKED: 'BLOCKED',
  SKIP: 'SKIP',
};

const RESULT_COLOR: Record<string, string> = {
  NOT_RUN: 'bg-gray-100 text-gray-600',
  PASS: 'bg-green-100 text-green-700',
  FAIL: 'bg-red-100 text-red-700',
  BLOCKED: 'bg-amber-100 text-amber-700',
  SKIP: 'bg-purple-100 text-purple-700',
};

const TYPE_LABEL: Record<string, string> = {
  POSITIVE: 'Positive',
  NEGATIVE: 'Negative',
  BOUNDARY: 'Boundary',
  ALTERNATIVE: 'Alternative',
};

const TYPE_COLOR: Record<string, string> = {
  POSITIVE: 'bg-green-50 text-green-700',
  NEGATIVE: 'bg-red-50 text-red-700',
  BOUNDARY: 'bg-purple-50 text-purple-700',
  ALTERNATIVE: 'bg-blue-50 text-blue-700',
};

const PRIORITY_LABEL: Record<string, string> = {
  BLOCKER: '블로커',
  CRITICAL: '긴급',
  HIGH: '높음',
  MEDIUM: '보통',
  LOW: '낮음',
};

const PRIORITY_COLOR: Record<string, string> = {
  BLOCKER: 'bg-red-100 text-red-800',
  CRITICAL: 'bg-orange-100 text-orange-800',
  HIGH: 'bg-amber-100 text-amber-800',
  MEDIUM: 'bg-blue-100 text-blue-700',
  LOW: 'bg-gray-100 text-gray-600',
};

// --- Helpers ---

function formatRunLabel(run: TestRunItem): string {
  if (run.status === 'COMPLETED' && run.completedAt) {
    return `${run.runNumber}차 테스트 (${new Date(run.completedAt).toLocaleDateString('ko-KR')} 완료)`;
  }
  if (run.status === 'ABORTED' && run.completedAt) {
    return `${run.runNumber}차 테스트 (${new Date(run.completedAt).toLocaleDateString('ko-KR')} 중단)`;
  }
  if (run.status === 'IN_PROGRESS') {
    return `${run.runNumber}차 테스트 (진행 중)`;
  }
  return `${run.runNumber}차 테스트 (준비 중)`;
}

function pct(part: number, total: number): number {
  return total > 0 ? Math.round((part / total) * 100) : 0;
}

// --- Sub-components ---

function HorizontalBar({
  label,
  pass,
  fail,
  total,
  labelColor,
}: {
  label: string;
  pass: number;
  fail: number;
  total: number;
  labelColor?: string;
}) {
  const passRate = pct(pass, total);
  const failRate = pct(fail, total);
  return (
    <div className="flex items-center gap-3">
      <span className={`w-24 text-sm font-medium shrink-0 ${labelColor || 'text-gray-700'}`}>
        {label}
      </span>
      <div className="flex-1 h-5 bg-gray-100 rounded-full overflow-hidden flex">
        {pass > 0 && (
          <div
            className="h-full bg-green-500 transition-all duration-300"
            style={{ width: `${passRate}%` }}
          />
        )}
        {fail > 0 && (
          <div
            className="h-full bg-red-400 transition-all duration-300"
            style={{ width: `${failRate}%` }}
          />
        )}
      </div>
      <span className="text-sm text-gray-600 w-32 text-right shrink-0">
        {pass}/{total} ({passRate}%)
      </span>
    </div>
  );
}

function SummaryCard({
  title,
  value,
  rate,
  color,
}: {
  title: string;
  value: number;
  rate?: number;
  color?: string;
}) {
  return (
    <div className="bg-white border border-gray-200 rounded-xl p-4 text-center">
      <div className="text-sm font-medium text-gray-500 mb-1">{title}</div>
      <div className={`text-2xl font-bold ${color || 'text-gray-900'}`}>{value}</div>
      {rate !== undefined && (
        <div className="text-sm text-gray-400 mt-0.5">{rate}%</div>
      )}
    </div>
  );
}

// --- Main page ---

export default function TestResultsPage() {
  const { specId } = useParams<{ specId: string }>();
  const { project } = useOutletContext<{ project: ProjectDetail }>();
  const navigate = useNavigate();

  // Run list
  const { data: runList, isLoading: isLoadingRuns } = useRunList(project.id, specId!);
  const [selectedRunId, setSelectedRunId] = useState('');

  // Set default to latest run
  useEffect(() => {
    if (!runList || runList.length === 0) {
      setSelectedRunId('');
      return;
    }
    if (!selectedRunId || !runList.some((r) => r.runId === selectedRunId)) {
      setSelectedRunId(runList[0].runId);
    }
  }, [runList, selectedRunId]);

  // Fetch detail & results for selected run
  const { data: runDetail, isLoading: isLoadingDetail } = useRunDetail(
    project.id,
    specId!,
    selectedRunId,
  );
  const { data: results, isLoading: isLoadingResults } = useRunResults(
    project.id,
    selectedRunId,
  );

  // Create run mutation for empty state
  const createRunMutation = useCreateTestRun(project.id);
  const [isStartingRun, setIsStartingRun] = useState(false);

  // Filter FAIL results
  const failResults = results?.filter((r) => r.status === 'FAIL') ?? [];

  // --- Handlers ---

  const handleRunChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    setSelectedRunId(e.target.value);
  };

  const handleStartTestRun = useCallback(async () => {
    if (!specId || isStartingRun) return;
    setIsStartingRun(true);
    try {
      const activeRun = await fetchActiveRun(project.id, specId);
      if (activeRun) {
        const confirmed = window.confirm(
          `${activeRun.runNumber}차 테스트가 진행 중입니다. 이어서 실행하시겠습니까?`,
        );
        if (confirmed) {
          navigate(
            `/projects/${project.projectKey}/specs/${specId}/test-runs/${activeRun.runId}`,
          );
        }
      } else {
        const run = await createRunMutation.mutateAsync(specId);
        navigate(
          `/projects/${project.projectKey}/specs/${specId}/test-runs/${run.runId}`,
        );
      }
    } catch (err: unknown) {
      const msg = (err as { response?: { data?: { error?: string } } })?.response?.data
        ?.error;
      toast.error(msg || '테스트 실행 시작에 실패했습니다.');
    } finally {
      setIsStartingRun(false);
    }
  }, [specId, isStartingRun, project.id, project.projectKey, createRunMutation, navigate]);

  // --- Loading ---

  if (isLoadingRuns) {
    return (
      <div className="flex items-center justify-center h-64">
        <Spinner className="size-8" />
      </div>
    );
  }

  const hasRuns = runList && runList.length > 0;

  return (
    <div>
      {/* Tab navigation */}
      <div className="flex border-b border-gray-200 mb-5">
        <Link
          to={`/projects/${project.projectKey}/tests`}
          state={{ specFilter: specId }}
          className="px-4 py-2.5 text-sm font-medium border-b-2 border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300 transition-colors"
        >
          테스트케이스
        </Link>
        <span className="px-4 py-2.5 text-sm font-medium border-b-2 border-primary-500 text-primary-600">
          테스트 결과
        </span>
      </div>

      {/* Header + run selector */}
      <div className="flex items-center justify-between mb-5">
        <h2 className="text-lg font-semibold text-gray-900">테스트 결과</h2>
        {hasRuns && (
          <div className="flex items-center gap-2">
            <label className="text-sm text-gray-600">회차:</label>
            <select
              value={selectedRunId}
              onChange={handleRunChange}
              className="px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-primary-500 outline-none bg-white min-w-[280px]"
            >
              {runList!.map((run) => (
                <option key={run.runId} value={run.runId}>
                  {formatRunLabel(run)}
                </option>
              ))}
            </select>
          </div>
        )}
      </div>

      {/* Empty state */}
      {!hasRuns && (
        <div className="flex flex-col items-center justify-center h-64 gap-4">
          <ClipboardList className="size-12 text-gray-300" />
          <p className="text-sm text-gray-500">실행 된 또는 실행 중인 테스트가 없습니다.</p>
          <button
            onClick={handleStartTestRun}
            disabled={isStartingRun || createRunMutation.isPending}
            className="inline-flex items-center gap-1.5 px-4 py-2 text-sm font-semibold text-white bg-primary-600 rounded-lg hover:bg-primary-700 disabled:opacity-50 transition-colors"
          >
            {isStartingRun ? <Spinner className="size-4" /> : <Play className="size-4" />}
            테스트 실행하기
          </button>
        </div>
      )}

      {/* Dashboard */}
      {hasRuns && (isLoadingDetail || isLoadingResults) && (
        <div className="flex items-center justify-center h-64">
          <Spinner className="size-8" />
        </div>
      )}

      {hasRuns && runDetail && results && (
        <Dashboard
          runDetail={runDetail}
          failResults={failResults}
          projectKey={project.projectKey}
        />
      )}
    </div>
  );
}

// --- Dashboard ---

function Dashboard({
  runDetail,
  failResults,
  projectKey,
}: {
  runDetail: {
    totalCount: number;
    passCount: number;
    failCount: number;
    blockedCount: number;
    skipCount: number;
    notRunCount: number;
    dashboard: {
      byType: Record<string, { total: number; pass: number; fail: number }>;
      byPriority: Record<string, { total: number; pass: number; fail: number }>;
      previousComparison: {
        previousRunNumber: number;
        passDiff: number;
        failDiff: number;
      } | null;
    };
  };
  failResults: TestRunResultItem[];
  projectKey: string;
}) {
  const { totalCount, passCount, failCount, notRunCount, blockedCount, skipCount } =
    runDetail;
  const passRate = pct(passCount, totalCount);
  const failRate = pct(failCount, totalCount);
  const blockedRate = pct(blockedCount, totalCount);
  const skipRate = pct(skipCount, totalCount);
  const notRunRate = pct(notRunCount, totalCount);

  const { byType, byPriority, previousComparison } = runDetail.dashboard;

  // Type ordering
  const typeOrder = ['POSITIVE', 'NEGATIVE', 'BOUNDARY', 'ALTERNATIVE'];
  const typeEntries = typeOrder
    .filter((t) => byType[t])
    .map((t) => ({ key: t, ...byType[t] }));

  // Priority ordering
  const priorityOrder = ['BLOCKER', 'CRITICAL', 'HIGH', 'MEDIUM', 'LOW'];
  const priorityEntries = priorityOrder
    .filter((p) => byPriority[p])
    .map((p) => ({ key: p, ...byPriority[p] }));

  return (
    <div className="space-y-5">
      {/* A) 실행 현황 바 */}
      <div className="bg-white border border-gray-200 rounded-xl p-4">
        <div className="h-4 bg-gray-100 rounded-full overflow-hidden flex mb-3">
          {passCount > 0 && (
            <div
              className="h-full bg-green-500"
              style={{ width: `${passRate}%` }}
              title={`PASS ${passRate}%`}
            />
          )}
          {failCount > 0 && (
            <div
              className="h-full bg-red-500"
              style={{ width: `${failRate}%` }}
              title={`FAIL ${failRate}%`}
            />
          )}
          {blockedCount > 0 && (
            <div
              className="h-full bg-amber-400"
              style={{ width: `${blockedRate}%` }}
              title={`BLOCKED ${blockedRate}%`}
            />
          )}
          {skipCount > 0 && (
            <div
              className="h-full bg-purple-400"
              style={{ width: `${skipRate}%` }}
              title={`SKIP ${skipRate}%`}
            />
          )}
        </div>
        <div className="flex items-center gap-4 text-sm">
          <span className="flex items-center gap-1.5">
            <span className="w-3 h-3 rounded-full bg-green-500" />
            PASS {passCount}
          </span>
          <span className="flex items-center gap-1.5">
            <span className="w-3 h-3 rounded-full bg-red-500" />
            FAIL {failCount}
          </span>
          <span className="flex items-center gap-1.5">
            <span className="w-3 h-3 rounded-full bg-amber-400" />
            BLOCKED {blockedCount}
          </span>
          <span className="flex items-center gap-1.5">
            <span className="w-3 h-3 rounded-full bg-purple-400" />
            SKIP {skipCount}
          </span>
          <span className="flex items-center gap-1.5">
            <span className="w-3 h-3 rounded-full bg-gray-300" />
            NOT_RUN {notRunCount}
          </span>
        </div>
      </div>

      {/* B) 요약 카드 */}
      <div className="grid grid-cols-4 gap-4">
        <SummaryCard title="총 TC" value={totalCount} />
        <SummaryCard
          title="PASS"
          value={passCount}
          rate={passRate}
          color="text-green-600"
        />
        <SummaryCard
          title="FAIL"
          value={failCount}
          rate={failRate}
          color="text-red-600"
        />
        <SummaryCard
          title="미실행"
          value={notRunCount}
          rate={notRunRate}
          color="text-gray-500"
        />
      </div>

      {/* C) 이전 회차 대비 */}
      {previousComparison && (
        <div className="bg-white border border-gray-200 rounded-xl p-4">
          <div className="text-sm font-medium text-gray-700 mb-2">
            이전 회차({previousComparison.previousRunNumber}차) 대비:
          </div>
          <div className="flex items-center gap-6 text-sm">
            <span className="flex items-center gap-1">
              PASS: {passCount - previousComparison.passDiff} → {passCount}{' '}
              <span
                className={`inline-flex items-center gap-0.5 font-medium ${
                  previousComparison.passDiff >= 0 ? 'text-green-600' : 'text-red-600'
                }`}
              >
                ({previousComparison.passDiff >= 0 ? '+' : ''}
                {previousComparison.passDiff})
                {previousComparison.passDiff > 0 ? (
                  <ArrowUp className="size-3.5" />
                ) : previousComparison.passDiff < 0 ? (
                  <ArrowDown className="size-3.5" />
                ) : null}
              </span>
            </span>
            <span className="flex items-center gap-1">
              FAIL: {failCount - previousComparison.failDiff} → {failCount}{' '}
              <span
                className={`inline-flex items-center gap-0.5 font-medium ${
                  previousComparison.failDiff <= 0 ? 'text-green-600' : 'text-red-600'
                }`}
              >
                ({previousComparison.failDiff >= 0 ? '+' : ''}
                {previousComparison.failDiff})
                {previousComparison.failDiff > 0 ? (
                  <ArrowUp className="size-3.5" />
                ) : previousComparison.failDiff < 0 ? (
                  <ArrowDown className="size-3.5" />
                ) : null}
              </span>
            </span>
          </div>
        </div>
      )}

      {/* D) 유형별 분포 */}
      {typeEntries.length > 0 && (
        <div className="bg-white border border-gray-200 rounded-xl p-4">
          <h3 className="text-sm font-semibold text-gray-900 mb-3">유형별 분포</h3>
          <div className="space-y-2.5">
            {typeEntries.map((entry) => (
              <HorizontalBar
                key={entry.key}
                label={TYPE_LABEL[entry.key] || entry.key}
                pass={entry.pass}
                fail={entry.fail}
                total={entry.total}
              />
            ))}
          </div>
        </div>
      )}

      {/* E) 우선순위별 분포 */}
      {priorityEntries.length > 0 && (
        <div className="bg-white border border-gray-200 rounded-xl p-4">
          <h3 className="text-sm font-semibold text-gray-900 mb-3">우선순위별 분포</h3>
          <div className="space-y-2.5">
            {priorityEntries.map((entry) => (
              <HorizontalBar
                key={entry.key}
                label={PRIORITY_LABEL[entry.key] || entry.key}
                pass={entry.pass}
                fail={entry.fail}
                total={entry.total}
              />
            ))}
          </div>
        </div>
      )}

      {/* F) FAIL TC 목록 */}
      <div className="bg-white border border-gray-200 rounded-lg overflow-hidden">
        <div className="px-4 py-3 border-b border-gray-200">
          <h3 className="text-sm font-semibold text-gray-900">
            FAIL TC 목록{' '}
            <span className="text-gray-400 font-normal">({failResults.length})</span>
          </h3>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-gray-50 border-b border-gray-200">
                <th className="px-4 py-3 text-left text-sm font-semibold text-gray-600 uppercase tracking-wider w-28">
                  TC ID
                </th>
                <th className="px-4 py-3 text-left text-sm font-semibold text-gray-600 uppercase tracking-wider">
                  제목
                </th>
                <th className="px-4 py-3 text-left text-sm font-semibold text-gray-600 uppercase tracking-wider w-24">
                  유형
                </th>
                <th className="px-4 py-3 text-left text-sm font-semibold text-gray-600 uppercase tracking-wider w-20">
                  우선순위
                </th>
                <th className="px-4 py-3 text-left text-sm font-semibold text-gray-600 uppercase tracking-wider w-32">
                  연결 작업
                </th>
                <th className="px-4 py-3 text-center text-sm font-semibold text-gray-600 uppercase tracking-wider w-24">
                  이전 결과
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {failResults.length === 0 ? (
                <tr>
                  <td colSpan={6} className="px-4 py-8 text-center text-gray-400">
                    FAIL 항목이 없습니다.
                  </td>
                </tr>
              ) : (
                failResults.map((r) => (
                  <tr key={r.resultId} className="hover:bg-gray-50 transition-colors">
                    <td className="px-4 py-3 font-mono text-sm text-gray-900 whitespace-nowrap">
                      {r.testCase.tcId}
                    </td>
                    <td className="px-4 py-3 text-gray-900">{r.testCase.title}</td>
                    <td className="px-4 py-3">
                      <span
                        className={`inline-flex items-center px-2 py-0.5 rounded text-sm font-medium ${TYPE_COLOR[r.testCase.type] || 'bg-gray-100 text-gray-600'}`}
                      >
                        {TYPE_LABEL[r.testCase.type] || r.testCase.type}
                      </span>
                    </td>
                    <td className="px-4 py-3">
                      <span
                        className={`inline-flex items-center px-2 py-0.5 rounded text-sm font-medium ${PRIORITY_COLOR[r.testCase.priority] || 'bg-gray-100 text-gray-600'}`}
                      >
                        {PRIORITY_LABEL[r.testCase.priority] || r.testCase.priority}
                      </span>
                    </td>
                    <td className="px-4 py-3">
                      {r.testCase.linkedIssues && r.testCase.linkedIssues.length > 0 ? (
                        <div className="flex flex-wrap gap-1">
                          {r.testCase.linkedIssues.map((issue) => (
                            <Link
                              key={issue.id}
                              to={`/projects/${projectKey}/issues?search=${issue.issueId}`}
                              className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[11px] font-medium bg-red-50 text-red-600 hover:bg-red-100 transition-colors"
                            >
                              <Bug className="size-3" />
                              {issue.issueId}
                            </Link>
                          ))}
                        </div>
                      ) : (
                        <span className="text-sm text-gray-400">-</span>
                      )}
                    </td>
                    <td className="px-4 py-3 text-center">
                      {r.previousStatus ? (
                        <span
                          className={`inline-flex items-center px-2 py-0.5 rounded text-sm font-medium ${RESULT_COLOR[r.previousStatus] || 'bg-gray-100 text-gray-600'}`}
                        >
                          {RESULT_LABEL[r.previousStatus] || r.previousStatus}
                        </span>
                      ) : (
                        <span className="text-sm text-gray-400">-</span>
                      )}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
